import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import './DocViewerModal.css';

// PDF viewer modal, ported from the labs_system bid system's FilePreviewModal.
// Our wiki sources are always PDFs (the ingest concatenates ZIPs into one), so
// there is no office->PDF conversion path here.
//
// The worker is a static asset in public/, copied from the EXACT pdfjs-dist
// version react-pdf depends on (5.4.296). A version mismatch makes <Document>
// fail at runtime. See craco.config.js.
//
// It MUST keep the .js extension: Amplify's SPA-fallback rewrite only exempts
// css|gif|ico|jpg|js|png|txt|svg|woff|woff2|ttf|map|json|html, so a .mjs worker
// is rewritten to index.html and pdfjs then loads HTML instead of the worker.
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const DocViewerModal = ({ file, initialPage, onClose }) => {
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [matchIdx, setMatchIdx] = useState(0);
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const width = Math.min((typeof window !== 'undefined' ? window.innerWidth : 1200) * 0.9, 1000);

  // Esc closes; Ctrl/Cmd+F focuses the in-document search.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Jump to a cited page. A one-shot scrollIntoView lands wrong because
  // react-pdf pages grow from placeholder height to real height, shifting the
  // target. So re-pin every 200ms and detect settling via offsetTop stability
  // plus canvas presence; bail out as soon as the user scrolls.
  useEffect(() => {
    if (!initialPage || !numPages) return;
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false, timer = null, ticks = 0, stable = 0, lastOffset = -1;
    const cleanup = () => { cancelled = true; if (timer) clearTimeout(timer); container.removeEventListener('wheel', cleanup); container.removeEventListener('touchmove', cleanup); };
    container.addEventListener('wheel', cleanup, { passive: true });
    container.addEventListener('touchmove', cleanup, { passive: true });

    const tick = () => {
      if (cancelled) return;
      const el = container.querySelector(`[data-pdf-page="${initialPage}"]`);
      if (el) {
        const offset = el.offsetTop;                    // scroll-independent
        const rendered = !!el.querySelector('canvas');  // actually painted
        stable = rendered && Math.abs(offset - lastOffset) < 2 ? stable + 1 : 0;
        lastOffset = offset;
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
      ticks++;
      if (stable >= 3 || ticks > 50) { cleanup(); return; }
      timer = setTimeout(tick, 200);
    };
    tick();
    return cleanup;
  }, [initialPage, numPages]);

  // Highlight matches across the rendered text layers. We highlight whole text
  // spans that contain the query (rather than measuring sub-span offsets) —
  // markedly simpler and less fragile than per-character measurement.
  const runSearch = useCallback((q) => {
    const container = containerRef.current;
    document.querySelectorAll('.dv-hit').forEach(el => el.classList.remove('dv-hit', 'dv-hit-current'));
    if (!container || q.trim().length < 2) { setMatches([]); setMatchIdx(0); return; }
    const needle = q.trim().toLowerCase();
    const hits = [];
    container.querySelectorAll('.react-pdf__Page__textContent span').forEach((span) => {
      if ((span.textContent || '').toLowerCase().includes(needle)) {
        span.classList.add('dv-hit');
        hits.push(span);
      }
    });
    setMatches(hits);
    setMatchIdx(0);
    if (hits[0]) {
      hits[0].classList.add('dv-hit-current');
      hits[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const gotoMatch = (dir) => {
    if (!matches.length) return;
    const next = (matchIdx + dir + matches.length) % matches.length;
    matches[matchIdx]?.classList.remove('dv-hit-current');
    matches[next]?.classList.add('dv-hit-current');
    matches[next]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setMatchIdx(next);
  };

  if (!file) return null;

  return createPortal(
    <div className="dv-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="dv-shell" onClick={(e) => e.stopPropagation()}>
        <header className="dv-head">
          <span className="dv-name" data-tip={file.filename}>{file.filename}</span>

          <div className="dv-search" data-tip="dv.search">
            <input
              ref={searchRef}
              type="search"
              placeholder="Sök i dokumentet…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); runSearch(e.target.value); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); gotoMatch(e.shiftKey ? -1 : 1); }
              }}
            />
            {query.trim().length >= 2 && (
              <span className="dv-count">
                {matches.length ? `${matchIdx + 1}/${matches.length}` : '0'}
              </span>
            )}
          </div>

          <a className="dv-btn" href={file.url} target="_blank" rel="noreferrer" data-tip="dv.download">Ladda ner</a>
          <button className="dv-close" onClick={onClose} aria-label="Stäng" data-tip="dv.close">×</button>
        </header>

        <div className="dv-body" ref={containerRef}>
          {error ? (
            <p className="dv-msg dv-error">
              Kunde inte visa dokumentet. <a href={file.url} target="_blank" rel="noreferrer">Ladda ner det i stället</a>.
            </p>
          ) : (
            <Document
              file={file.url}
              onLoadSuccess={({ numPages: n }) => { setNumPages(n); setLoading(false); }}
              onLoadError={(e) => { console.error('PDF load error', e); setError(e); setLoading(false); }}
              loading={<p className="dv-msg">Laddar dokument…</p>}
            >
              {Array.from(new Array(numPages), (_, i) => (
                <div key={i} data-pdf-page={i + 1} className="dv-page">
                  <Page
                    pageNumber={i + 1}
                    width={width}
                    renderTextLayer={true}
                    renderAnnotationLayer={false}
                    onRenderTextLayerSuccess={() => { if (query.trim().length >= 2) runSearch(query); }}
                  />
                  <div className="dv-pageno">{i + 1} / {numPages}</div>
                </div>
              ))}
            </Document>
          )}
          {loading && !error && <p className="dv-msg">Laddar dokument…</p>}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DocViewerModal;
